import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

export const useSettings = () => {
  const navigate = useNavigate();
  const [admissionFee, setAdmissionFee] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAdmissionFee = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${domainSettings.urls.enrollment.settings}/by-property?property=ADMISSION_FEE`;
      const response = await Utilities.fetchWithAuth(
        "GET",
        url,
        null,
        navigate,
      );

      if (response.ok) {
        const data = response.data || response;
        setAdmissionFee(data);
        return data;
      } else {
        // If setting doesn't exist, we'll show an appropriate message
        if (response.status === 404) {
          message.warning(
            "Admission fee setting not found. Please create it first.",
          );
        } else {
          Utilities.showError(response, "Failed to fetch admission fee");
        }
        return null;
      }
    } catch (error) {
      message.error("An error occurred while fetching admission fee");
      console.error(error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const updateAdmissionFee = useCallback(
    async (newAmount, feeId = null) => {
      // Use provided feeId or fall back to the state
      const settingId = feeId || (admissionFee && admissionFee.id);

      if (!settingId) {
        message.error("Cannot update: Admission fee setting ID not found");
        return false;
      }

      try {
        const response = await Utilities.fetchWithAuth(
          "PATCH",
          `${domainSettings.urls.enrollment.settings}/${settingId}`,
          { value: newAmount.toString() },
          navigate,
        );

        if (response.ok) {
          return true;
        } else {
          Utilities.showError(response, "Failed to update admission fee");
          return false;
        }
      } catch (error) {
        message.error("An error occurred while updating admission fee");
        console.error(error);
        return false;
      }
    },
    [navigate, admissionFee],
  );

  const createAdmissionFee = useCallback(
    async (amount) => {
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.enrollment.settings,
          {
            property: "ADMISSION_FEE",
            value: amount.toString(),
            is_public: 0,
            description:
              "Default admission application fee charged to all applicants",
          },
          navigate,
        );

        if (response.ok) {
          const newSetting = response.data || response;
          setAdmissionFee(newSetting); // Update state immediately
          return true;
        } else {
          Utilities.showError(
            response,
            "Failed to create admission fee setting",
          );
          return false;
        }
      } catch (error) {
        message.error("An error occurred while creating admission fee setting");
        console.error(error);
        return false;
      }
    },
    [navigate, fetchAdmissionFee],
  );

  return {
    admissionFee,
    loading,
    fetchAdmissionFee,
    updateAdmissionFee,
    createAdmissionFee,
  };
};
